/* The ids row (2b-1) · what the server holds, kept in memory for this tab when the build names an API (VITE_API_URL).
   lib/server.tsx reads it once at the app's root (ServerMirror, App.tsx); the getters of lib/wheel, lib/asks, lib/campaign
   and lib/scope return it then, and their per-world stores (bmr.*) otherwise. Nothing here touches localStorage: with an
   API a demo world is never read for these, never written, never overwritten with server data. What the app changes in
   API mode lands here, and a reload reads the server again. 2b-2: what an editor changes lands here at once and is sent to
   the server (edited → lib/writes), whose answer replaces it. */
import { apiWrites, type Location } from './api'
import type { GiftType, Roue, WheelDesign } from './wheel'
import type { Ask, LinkAsk } from './asks'
import type { Campaign, DestinationRef, Fiche, SocialId, SocialRef } from './campaign'
import type { IdentityFields, SecteurId } from './scope'
import type { Tag, TagKind } from './tags'
import type { Category, Place } from './organisation'
import type { Member } from './team'

/** An establishment as the server answers it (GET /v1/app/locations): the fields the demo keeps per world in its own stores. */
export interface ServerLocation extends Location {
  placeId: string | null; reviewUrl: string | null; bookingUrl: string | null; windowDays: number | null; address: string | null
}
/** 2b-2 · the workspace's settings (GET /v1/app/workspace, 02 §2.3): the sector, what the customer fills in, the look of
    every roue. The demo keeps them per world (lib/scope, lib/wheel). */
export interface ServerWorkspace { sector: SecteurId | null; identityFields: IdentityFields; wheelDesign: WheelDesign | null }
export interface Mirror {
  locations: ServerLocation[]; destinations: DestinationRef[]; socials: SocialRef[]
  workspace: ServerWorkspace | null
  types: GiftType[]; roues: Roue[]; asks: Ask[]; campaigns: Campaign[]
  /** lib-pack-19 · the merchant's saved « Demande d’avis » / « Réseaux sociaux » presets (GET /v1/app/library, 04 §4.4):
      one list per workspace, in the order sent; the demo keeps them per world (lib/asks › bmr.lienasks) */
  library: LinkAsk[]
  /** the Google fiche read for each establishment (GET …/locations/:id/fiche), null before one is read */
  fiches: Record<string, Fiche | null>
  /** the roue a new campaign starts from: this tab's choice, the first roue otherwise (the demo keeps it per world) */
  activeRoueId: string | null
  /** I-1 · establishment id → its « campagne principale » (the rows' `principal`, 05 §5.4; the demo keeps it per world) */
  principals: Record<string, string>
  /** 2b-2 · a gift type, a roue or a demande made in this tab, under the id the server gave it: the first id keeps
      finding it (serverIdOf) */
  aliases: Record<string, string>
  /** the members row · the people of Équipe (GET /v1/app/members, 09 §9.1): each one's establishment, or none for the
      whole workspace (the owner's own); the demo keeps them per world (lib/team) */
  members: Member[]
  /** lib-pack-21 · the workspace's « Étiquettes » (GET /v1/app/tags, 02 §2.5), with the server's counts; the demo keeps
      them per world (lib/tags › bmr.tags) */
  tags: Tag[]
  /** lib-pack-21 · each item's étiquettes under the server's id (a campaign row's `tags`, a support row's): the tab's copy,
      written with PATCH { tags } on the item */
  itemTags: { campaigns: Record<string, string[]>; supports: Record<string, string[]> }
  /** lib-pack-23 · the workspace's « Catégories » and « Emplacements » (GET /v1/app/categories, /v1/app/places, 02 §2.6,
      0039), with the server's counts; the demo keeps them per world (lib/organisation › bmr.organisation) */
  categories: Category[]; places: Place[]
  /** lib-pack-23 · each campaign's catégorie and each support's emplacement under the server's id (the rows' `categoryId`,
      `placeId`; null = none): the tab's copy, written with PATCH { categoryId } / { placeId } on the item */
  itemCategory: Record<string, string | null>; itemPlace: Record<string, string | null>
  /** lib-pack-23 · whether the server answered the two lists (an API without 0039 answers 404: false, the lists stay empty) */
  orgReady: boolean
}
const EMPTY: Mirror = { locations: [], destinations: [], socials: [], workspace: null, types: [], roues: [], asks: [], campaigns: [], library: [], fiches: {}, activeRoueId: null, principals: {}, aliases: {}, members: [], tags: [], itemTags: { campaigns: {}, supports: {} },
  categories: [], places: [], itemCategory: {}, itemPlace: {}, orgReady: false }
let state: Mirror = EMPTY

export const mirror = (): Mirror => state
/** A screen that reads the mirror through a hook (lib/scope) hears each change: an answer, a refusal read back. */
const listeners = new Set<() => void>()
export const subscribeMirror = (l: () => void): (() => void) => { listeners.add(l); return () => { listeners.delete(l) } }
/** `quiet`: the copy changes without waking the screens (lib-pack-21: a row's étiquettes noted during a list's render). */
export function setMirror(patch: Partial<Mirror>, quiet = false): void { state = { ...state, ...patch }; if (!quiet) listeners.forEach(l => l()) }
/** The id the server knows for one this tab made (a gift type, a roue, a demande); any other id as it is. */
export const serverIdOf = (id: string): string => state.aliases[id] ?? id

/* 2b-2 · the editors' writes. With an API, a setter of lib/wheel, lib/asks, lib/campaign, lib/scope or lib/models changes
   the mirror at once, then says what changed here; lib/writes sends it to the server, one write at a time, in order, and
   puts the server's answer in its place. In the demo nothing is said. */
export type Edit =
  | { kind: 'type'; id: string }
  | { kind: 'roues'; before: Roue[] }
  | { kind: 'ask'; id: string } | { kind: 'ask-archive'; id: string }
  /** lib-pack-19 · the whole library, replaced in one PUT (04 §4.4: last write wins) */
  | { kind: 'library' }
  /** lib-pack-21 · the whole « Étiquettes » list, replaced in one PUT (02 §2.5, diffed by the server); « Fusionner »; an item's own */
  | { kind: 'tags' } | { kind: 'tag-merge'; from: string; into: string } | { kind: 'item-tags'; item: TagKind; id: string }
  /** lib-pack-23 · a whole list replaced in one PUT (02 §2.6, diffed by the server); « Fusionner »; a campaign's catégorie, a support's emplacement */
  | { kind: 'categories' } | { kind: 'category-merge'; from: string; into: string } | { kind: 'item-category'; id: string }
  | { kind: 'places' } | { kind: 'place-merge'; from: string; into: string } | { kind: 'item-place'; id: string }
  | { kind: 'platform'; ref: DestinationRef } | { kind: 'platform-drop'; ref: DestinationRef }
  | { kind: 'social'; locationId: string; social: SocialId } | { kind: 'social-drop'; locationId: string; social: SocialId }
  /** lib-pack-22 · `reviewUrl` = « Lien d'avis Google » edited on its own (02 §2.2, wireframes ruling 20/10 § 9) */
  | { kind: 'location'; id: string; fields: Array<'name' | 'address' | 'bookingUrl' | 'windowDays' | 'reviewUrl'> }
  | { kind: 'workspace'; fields: Array<'sector' | 'identityFields' | 'wheelDesign'> }
  | { kind: 'fiche'; locationId: string; url: string }
  | { kind: 'model'; sector: SecteurId; locationId: string }
let sink: ((e: Edit) => void) | null = null
export const setEditSink = (f: (e: Edit) => void): void => { sink = f }
export const edited = (e: Edit): void => { if (apiWrites) sink?.(e) }
/** I-1 · a campaign row the server answered (a read, « Mettre en ligne », a pause, an end, « Définir comme campagne
    principale ») says where this campaign is the principale now: those establishments are its own, and no other one is. */
export function principalsOfRow(row: { id: string; principal: string[] }): void {
  const m = Object.fromEntries(Object.entries(state.principals).filter(([, c]) => c !== row.id))
  for (const l of row.principal) m[l] = row.id
  state = { ...state, principals: m }
}

/** The establishment a screen falls back to: the demo's « l0 », with an API the server's first establishment. */
export const homeLocationId = (): string => (apiWrites ? state.locations[0]?.id ?? '' : 'l0')
/** An establishment id the server knows. With an API, an id it does not know (a demo id still kept in this browser: a
    member's « l0 », the builder's memo) reads as its first establishment; in the demo every id passes as it is. */
export const serverLocationId = (id: string | null | undefined): string =>
  !apiWrites ? id ?? 'l0' : id && state.locations.some(l => l.id === id) ? id : homeLocationId()

/* The server writes of this tab, one at a time, in the order they were asked: a draft's first save names it (`cmp_…`),
   and every later write — its next save, « Mettre en ligne », « Annuler les modifications » — finds it under that name. */
let writes: Promise<unknown> = Promise.resolve()
export function queueWrite<T>(fn: () => Promise<T>): Promise<T> {
  const run = writes.then(fn)
  writes = run.catch(() => undefined)
  return run
}
